import { ChangeEvent, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  ClickOutsideDetector,
  IconClose,
  IconDown,
  IconSelected,
  IconUp,
  InputSize,
  InputText,
} from '@ui/src';
import { SourceOfWealthTypes } from './types';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';

import cn from '../aml.module.css';

const SourceOfWealthItem = ({
  sourcesOfWealthForIndividual,
  sourceOfWealthForIndividualFiltered,
  sourceOfWealthForIndividual,
  sourceOfWealthForIndividualValues,
  setValue,
  baseCurrency,
  control,
  errors,
  clearErrors,
  status,
}: SourceOfWealthTypes) => {
  const [formValues, setFormValues] = useState<{
    id?: string;
    sourceId: string;
    value: number | string;
  }>();
  const [listVisible, setListVisible] = useState(false);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextFormValues = { ...formValues };

    const nextSourceOfWealthForIndividualValues =
      sourceOfWealthForIndividualValues?.map((item) => {
        const result = { ...item };

        if (result.id === sourceOfWealthForIndividual?.id) {
          if (e.target.name === `sourceId-${result.id}`) {
            result.sourceId = e.target.value;
            nextFormValues.sourceId = e.target.value;
          } else if (e.target.name === `value-${result.id}`) {
            result.value = e.target.value;
            nextFormValues.value = e.target.value;
          }
        }

        return result;
      });

    setFormValues(
      nextFormValues as {
        id?: string;
        sourceId: string;
        value: number | string;
      },
    );
    setValue(
      'sourcesAndSizesOfWealth',
      nextSourceOfWealthForIndividualValues as {
        sourceId: string;
        value: number | string;
      }[],
    );
  };

  const onDelete = () => {
    setValue(
      'sourcesAndSizesOfWealth',
      sourceOfWealthForIndividualValues?.filter(
        (item) =>
          item?.sourceId !== sourceOfWealthForIndividual?.sourceId &&
          item?.id !== sourceOfWealthForIndividual?.id,
      ),
    );
  };

  useEffect(() => {
    if (sourceOfWealthForIndividual) {
      setFormValues(sourceOfWealthForIndividual);
    }
  }, [sourceOfWealthForIndividual]);

  return (
    <div className={cn.sourceOfIncomeItem}>
      <div className={cn.sourceOfIncomeItemTitle}>
        <ClickOutsideDetector setOptionsListVisible={setListVisible}>
          <>
            <button
              type="button"
              className={cn.sourceOfIncomeButton}
              onClick={() => setListVisible(!listVisible)}
              disabled={status === CustomerStatus.CLOSED}
            >
              <span>
                {formValues?.sourceId
                  ? sourcesOfWealthForIndividual?.find(
                      (item) => item.id === formValues?.sourceId,
                    )?.value
                  : 'Select option'}
              </span>
              {status !== CustomerStatus.CLOSED && (
                <div className={cn.iconWrapper}>
                  {listVisible ? (
                    <IconUp width={13} height={13} />
                  ) : (
                    <IconDown width={13} height={13} />
                  )}
                </div>
              )}
            </button>
            {listVisible && (
              <div className={cn.sourcesOfIncomeValuesList}>
                {formValues?.sourceId && (
                  <button
                    type="button"
                    className={`${cn.sourcesOfIncomeValuesButton} ${cn.active}`}
                  >
                    {
                      sourcesOfWealthForIndividual?.find(
                        (item) => item.id === formValues?.sourceId,
                      )?.value
                    }
                    <IconSelected />
                  </button>
                )}
                {sourceOfWealthForIndividualFiltered
                  ?.filter((item) => item.id !== formValues?.sourceId)
                  ?.map((item) => (
                    <button
                      type="button"
                      className={cn.sourcesOfIncomeValuesButton}
                      key={item.id}
                      onClick={() =>
                        onChange({
                          target: {
                            name: `sourceId-${sourceOfWealthForIndividual?.id}`,
                            value: item.id,
                          },
                        } as ChangeEvent<HTMLInputElement>)
                      }
                    >
                      {item.value}
                    </button>
                  ))}
              </div>
            )}
          </>
        </ClickOutsideDetector>
      </div>
      <div className={cn.sourceOfIncomeValue}>
        <InputText
          name={`value-${sourceOfWealthForIndividual?.id}`}
          placeholder="Enter annual income..."
          value={formValues?.value}
          size={InputSize.SMALL}
          className="borderless"
          onChange={(e) =>
            onChange({
              target: {
                name: `value-${sourceOfWealthForIndividual?.id}`,
                value: e.target.value,
              },
            } as ChangeEvent<HTMLInputElement>)
          }
          currency={baseCurrency}
          disabled={status === CustomerStatus.CLOSED}
          amountInput
        />
        {(sourceOfWealthForIndividualValues?.length as number) > 1 &&
          status !== CustomerStatus.CLOSED && (
            <button
              type="button"
              className={cn.removeSourceOfIncomeButton}
              onClick={onDelete}
            >
              <IconClose width={16} height={16} />
            </button>
          )}
      </div>
      {formValues?.sourceId === '98cdb129-3170-4ff0-8dd9-c0603aafe37c' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <RegulatedCompanyRow title="Specify other source" withoutPadding>
            <Controller
              control={control}
              name="sourcesAndSizesOfWealthOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    clearErrors(['sourcesAndSizesOfWealthOther']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.sourcesAndSizesOfWealthOther?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
        </div>
      )}
      {formValues?.sourceId === 'f1e7067d-3053-460e-a7c4-60ef897f0878' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <RegulatedCompanyRow title="Specify other source" withoutPadding>
            <Controller
              control={control}
              name="sourcesAndSizesOfWealthBorrowedOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    clearErrors(['sourcesAndSizesOfWealthBorrowedOther']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.sourcesAndSizesOfWealthBorrowedOther?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
        </div>
      )}
    </div>
  );
};

export default SourceOfWealthItem;
